/* eslint-disable no-param-reassign */
import {
  dissoc, assoc, equals, cond,
} from 'ramda';
import uuid from 'uuid/v4';
import { INPUT_TYPES } from 'app-constants/inputTypes';
import {
  ACTION_TYPES, CALL_API, CALL_FUNCTION,
  COMMENT, MAP_RESULT, SEND_MESSAGE,
} from 'app-constants/actionTypes';
import { VALUE_TIME_CAP, VALUE_TIME_OUT } from 'app-constants/apiIntegrations';
import { isJson } from 'utils';
import { getBotspecInfo } from '../selectors';
import { updateBotspecs } from './chatbots';
import {
  createText, createEmail, createPassword,
  createCheckbox, createRadio, createMedia,
  createDate, createLocation, createPhone,
  createNumber, createBarcode, createSignature,
} from './inputTypes';
import slugify from './utils';

export const NODE_TYPES = {
  ENTRY_POINT: 'Entry Point',
  ...ACTION_TYPES,
};

export const CONTROL_ACTIONS = [
  {
    title: 'HOME_CHATBOTS_CANCEL',
    icon: 'fas fa-times fa-fw',
    type: INPUT_TYPES.CANCEL,
  },
];

const createSendMessageProperties = () => ({
  title: '',
  type: 'send_message',
  vars: {},
  messages: {},
  input: {},
  mediaUrl: '',
  metadata: {
    media: '',
  },
  closeChat: false,
  delay: 0,
  resultVariables: {},
});

const createCallApiProperties = () => ({
  type: 'call_api',
  vars: {},
  method: '',
  content_type: '',
  url: '',
  params: {},
  headers: {},
  body: '',
  timeout: VALUE_TIME_OUT,
  postVerify: {
    timecap: VALUE_TIME_CAP,
    status: ['200 OK', '201 Created'],
    schema: JSON.stringify({
      type: 'object',
    }),
  },
  resultVariables: {},
});

const createMapResultProperties = () => ({
  title: '',
  type: 'map_result',
  vars: {},
  targets: {},
});

const createCallFunctionProperties = () => ({
  type: 'call_function',
  vars: {},
  eval: {},
  resultVariables: {},
});

const createEvalNextProperties = () => ({
  type: 'eval_next',
  vars: {},
  eval: {},
  resultVariables: {},
});

const createConditionalStepProperties = () => ({
  type: 'conditional_step',
  vars: {},
  eval: {},
  resultVariables: {},
  conditions: {},
  isEvalFunction: true,
});

const createStoreDataProperties = () => ({
  type: 'store_data',
  vars: {},
  eval: {},
  resultVariables: {},
});

const createStoreFileProperties = () => ({
  type: 'store_file',
  vars: {},
  eval: {},
  resultVariables: {},
});

const createCommentProperties = () => ({
  type: COMMENT,
  text: '',
});

export const addNode = (nodeId, type) => (dispatch, getState, getContext) => {
  const { selectedBotspec, botspecs, botspec } = getBotspecInfo(getState());
  const nodes = botspec.rete_json.data;
  const { editor } = getContext();
  if (nodes && !nodes[nodeId]) {
    const createProperty = cond([
      [equals(ACTION_TYPES.SEND_MESSAGE), createSendMessageProperties],
      [equals(ACTION_TYPES.CALL_API), createCallApiProperties],
      [equals(ACTION_TYPES.MAP_RESULT), createMapResultProperties],
      [equals(ACTION_TYPES.CALL_FUNCTION), createCallFunctionProperties],
      [equals(ACTION_TYPES.EVAL_NEXT), createEvalNextProperties],
      [equals(ACTION_TYPES.STORE_DATA), createStoreDataProperties],
      [equals(ACTION_TYPES.STORE_FILE), createStoreFileProperties],
      [equals(ACTION_TYPES.CONDITIONAL_STEP), createConditionalStepProperties],
      [equals(COMMENT), createCommentProperties],
    ]);
    const reteNode = editor && editor.nodes ? editor.nodes.find((node) => node.id === nodeId) : {};
    const properties = { ...createProperty(type), title: reteNode.data.title };
    const data = { ...nodes, [nodeId]: { properties, connections: {} } };
    const reteJson = { ...botspec.rete_json, data };
    if (reteNode.data) {
      reteNode.data.properties = { ...reteNode.data.properties, ...properties };
      reteNode.update();
    }

    dispatch(
      updateBotspecs({
        ...botspecs,
        [selectedBotspec]: {
          ...botspec,
          rete_json: reteJson,
        },
      }),
    );
  }
};

export const deleteNode = (nodeId) => (dispatch, getState) => {
  const { selectedBotspec, botspecs, botspec } = getBotspecInfo(getState());
  let { rete_json: reteJson } = botspec;
  const nodes = reteJson.data;

  if (reteJson.entryPoint === nodeId) {
    reteJson = { ...reteJson, entryPoint: '' };
  }
  const data = dissoc(nodeId, nodes);
  const updatedReteJson = { ...reteJson, data };
  dispatch(updateBotspecs(
    { ...botspecs, [selectedBotspec]: { ...botspec, rete_json: updatedReteJson } },
  ));
};

const formatMapResultVariables = (oldTitle, newTitle, oldNodeId, nodeId, properties) => {
  const oldTransitionId = slugify(`${oldTitle}${oldNodeId}`);
  const newTransitionId = slugify(`${newTitle}${nodeId}`);

  let updatedResultVariables = properties.resultVariables;
  if (properties.resultVariables) {
    updatedResultVariables = Object.keys(properties.resultVariables).reduce((accum, key) => ({
      ...accum,
      [key]: {
        ...properties.resultVariables[key],
        reference: properties.resultVariables[key].reference.replace(
          oldTransitionId, newTransitionId,
        ),
      },
    }), properties.resultVariables);
  }

  return { ...properties, resultVariables: updatedResultVariables };
};

export const saveNodeProperties = (nodeId, properties) => (
  dispatch,
  getState,
  getContext,
) => {
  const { selectedBotspec, botspecs, botspec } = getBotspecInfo(getState());
  const { editor } = getContext();
  const reteNode = editor && editor.nodes ? editor.nodes.find((node) => node.id === nodeId) : {};
  const nodes = botspec.rete_json.data;
  let node = nodes[nodeId];
  // use formatMapResultVariables
  const oldTitle = node.properties.title;
  const newTitle = properties.title;
  const updatedProperties = formatMapResultVariables(
    oldTitle, newTitle, nodeId, nodeId, properties,
  );
  node = assoc('properties', updatedProperties, node);
  if (reteNode && reteNode.data) {
    const title = properties.title || reteNode.data.title;
    reteNode.data.properties = updatedProperties;
    reteNode.data.title = title;
    reteNode.update();
  }
  const data = { ...nodes, [nodeId]: { ...node } };
  const reteJson = { ...botspec.rete_json, data };
  dispatch(updateBotspecs({ ...botspecs, [selectedBotspec]: { ...botspec, rete_json: reteJson } }));
};

export const deleteNodeProperty = (nodeId, type) => (dispatch, getState, getContext) => {
  const { selectedChatbot, chatbots, chatbot } = getBotspecInfo(getState());
  const { editor } = getContext();
  const reteNode = editor && editor.nodes ? editor.nodes.find((node) => node.id === nodeId) : {};
  const nodes = chatbot.reteJson.data;
  let node = nodes[nodeId];
  const properties = node.properties.filter((prop) => prop.type !== type);
  node = assoc('properties', properties, node);
  if (reteNode.data) {
    reteNode.data.properties = properties;
    reteNode.update();

    editor.view.updateConnections({ node: reteNode });
  }
  const data = { ...nodes, [nodeId]: { ...node } };
  const reteJson = { ...chatbot.reteJson, data };
  dispatch(updateBotspecs({ ...chatbots, [selectedChatbot]: { ...chatbot, reteJson } }));
};

export const saveConnection = (nodeId, nodeInputId, title) => (dispatch, getState) => {
  const { selectedChatbot, chatbots, chatbot } = getBotspecInfo(getState());
  const nodes = chatbot.reteJson.data;
  let node = nodes[nodeId];
  const connections = { ...node.connections, [nodeInputId]: title };
  node = assoc('connections', connections, node);
  const data = { ...nodes, [nodeId]: { ...node } };
  const reteJson = { ...chatbot.reteJson, data };
  dispatch(updateBotspecs({ ...chatbots, [selectedChatbot]: { ...chatbot, reteJson } }));
};

export const deleteConnection = (nodeId, nodeInputId) => (dispatch, getState) => {
  const { selectedChatbot, chatbots, chatbot } = getBotspecInfo(getState());
  const nodes = chatbot.reteJson.data;
  let node = nodes[nodeId];
  if (node && node.connections) {
    const connections = dissoc(nodeInputId, node.connections);
    node = assoc('connections', connections, node);
    const data = { ...nodes, [nodeId]: { ...node } };
    const reteJson = { ...chatbot.reteJson, data };
    dispatch(
      updateBotspecs({ ...chatbots, [selectedChatbot]: { ...chatbot, reteJson } }),
    );
  }
};

export const addVariable = (name, value) => (dispatch, getState) => {
  const { selectedChatbot, chatbots, chatbot } = getBotspecInfo(getState());
  const reteJson = {
    ...chatbots.reteJson,
    variables: { ...chatbot.reteJson.variables, [uuid()]: { name, value } },
  };
  dispatch(updateBotspecs({ ...chatbots, [selectedChatbot]: { ...chatbots, reteJson } }));
};

const deleteOutputConnections = (output, editor) => {
  output.connections.map((connection) => editor.removeConnection(connection));
};

export const deleteNodeOutput = (nodeId, keys) => async (dispatch, getState, getContext) => {
  const { editor, createOutput } = getContext();
  const reteNode = editor && editor.nodes ? editor.nodes.find((node) => node.id === nodeId) : {};
  if (reteNode) {
    keys.forEach((key) => {
      const output = reteNode.outputs.get(key);
      if (output) {
        deleteOutputConnections(output, editor);
        reteNode.removeOutput(output);
      }
    });
    if (reteNode.data.outputs && reteNode.data.outputs.length > 0) {
      reteNode.data.outputs = reteNode.data.outputs.filter((output) => !keys.includes(output));
    }
  }

  if (reteNode.outputs.size === 0 && !keys.find((key) => key === 'nxt')) {
    const output = createOutput('nxt', 'Next');
    reteNode.addOutput(output);
  }

  await reteNode.update();
  editor.view.updateConnections({ node: reteNode });
};

export const deleteNodeOutputs = (nodeId) => (dispatch, getState, getContext) => {
  const { editor, createOutput } = getContext();
  const reteNode = editor && editor.nodes ? editor.nodes.find((node) => node.id === nodeId) : {};
  const defaultOutput = reteNode.outputs.get('nxt');
  if (reteNode && reteNode.data.outputs && reteNode.data.outputs.length > 0) {
    reteNode.data.outputs.forEach((key) => {
      const output = reteNode.outputs.get(key);
      deleteOutputConnections(output, editor);
    });
    reteNode.outputs.clear();
    reteNode.data.outputs = [];

    if (!defaultOutput) {
      const output = createOutput('nxt', 'Next');
      reteNode.data.outputs.push('nxt');
      reteNode.addOutput(output);
    }

    reteNode.update();
  }
};

export const addNodeOutput = (nodeId, key, label) => async (dispatch, getState, getContext) => {
  const { editor, createOutput } = getContext();
  const reteNode = editor && editor.nodes ? editor.nodes.find((node) => node.id === nodeId) : {};
  const defaultOutput = reteNode.outputs.get('nxt');

  if (reteNode) {
    if (defaultOutput) {
      dispatch(deleteNodeOutput(nodeId, ['nxt']));
    }

    if (!reteNode.data.outputs) {
      reteNode.data.outputs = [];
    }

    if (!reteNode.data.outputs.find((output) => output === key)) {
      reteNode.data.outputs.push(key);
      reteNode.data.outputsLabels = { ...reteNode.data?.outputsLabels, [key]: label };
      const output = createOutput(key, label);
      reteNode.addOutput(output);
      await reteNode.update();
    }
  }
};

export const deleteVariable = (id) => (dispatch, getState) => {
  const { selectedChatbot, chatbots, chatbot } = getBotspecInfo(getState());
  const reteJson = { ...chatbot.reteJson, variables: dissoc(id, chatbot.reteJson.variables) };
  dispatch(updateBotspecs({ ...chatbots, [selectedChatbot]: { ...chatbot, reteJson } }));
};

export const setEntryPoint = (nodeId) => (dispatch, getState, getContext) => {
  const { selectedBotspec, botspecs, botspec } = getBotspecInfo(getState());
  const { editor } = getContext();
  const reteNode = editor && editor.nodes ? editor.nodes.find((node) => node.id === nodeId) : {};
  const remainingNodes = editor && editor.nodes
    ? editor.nodes.filter((node) => node.id !== nodeId) : [];
  if (reteNode.data) {
    reteNode.data = { ...reteNode.data, isEntryPoint: true };
    reteNode.update();
  }

  if (remainingNodes.length > 0) {
    remainingNodes.forEach((node) => {
      node.data = { ...node.data, isEntryPoint: false };
      node.update();
    });
  }

  const reteJson = { ...botspec.rete_json, entryPoint: nodeId };

  dispatch(
    updateBotspecs({ ...botspecs, [selectedBotspec]: { ...botspec, rete_json: reteJson } }),
  );
};

export const createInputTypeProperty = (nodeId, properties, type) => (dispatch) => {
  const createProperty = cond([
    [equals(INPUT_TYPES.TEXT), createText],
    [equals(INPUT_TYPES.EMAIL), createEmail],
    [equals(INPUT_TYPES.PASSWORD), createPassword],
    [equals(INPUT_TYPES.CHECKBOX), createCheckbox],
    [equals(INPUT_TYPES.RADIO), createRadio],
    [equals(INPUT_TYPES.MEDIA), createMedia],
    [equals(INPUT_TYPES.DATE), createDate],
    [equals(INPUT_TYPES.LOCATION), createLocation],
    [equals(INPUT_TYPES.PHONE), createPhone],
    [equals(INPUT_TYPES.NUMBER), createNumber],
    [equals(INPUT_TYPES.BARCODE), createBarcode],
    [equals(INPUT_TYPES.SIGNATURE), createSignature],
  ]);

  const inputType = createProperty(type);
  const updatedProperties = { ...properties, input: inputType };
  dispatch(saveNodeProperties(nodeId, updatedProperties));
};

const getCallApiOutputType = (actionProperties) => {
  const { schema: jsonSchema } = actionProperties.postVerify;
  let schema = '{}';
  try {
    schema = isJson(jsonSchema) ? JSON.parse(jsonSchema) : jsonSchema;
    return schema.type;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }

  return '';
};

const getCallFunctionOutputType = (actionProperties) => {
  const { outputType, schema: schemaString } = actionProperties.eval;

  let schema;
  if (schemaString?.length > 0) {
    try {
      schema = JSON.parse(schemaString);
    } catch (error) {
      // Ignore
    }
  }

  return schema?.type || outputType;
};

export const getActionAnswerType = (actionProperties) => {
  const getActionOutputTypeCalculator = {
    [CALL_API]: getCallApiOutputType,
    [CALL_FUNCTION]: getCallFunctionOutputType,
  };

  return getActionOutputTypeCalculator[actionProperties.type](actionProperties);
};

export const assignName = (type, nodes) => {
  let count = 0;
  if (nodes.length > 0) {
    nodes.forEach((node) => {
      if (node.name === type) {
        count += 1;
      }
    });
  }
  return count;
};

const formatExplicitMapResultVariables = (oldTitle, newTitle, oldNodeId, nodeId, properties) => {
  const oldTransitionId = slugify(`${oldTitle}${oldNodeId}`);
  const newTransitionId = slugify(`${newTitle}${nodeId}`);

  const { targets } = properties;
  const updatedTargets = Object.keys(targets).reduce(
    (accum, targetKey) => {
      const target = targets[targetKey];
      return {
        ...accum,
        [targetKey]: {
          from: target.from.replace(oldTransitionId, newTransitionId),
          assignments: target.assignments.reduce((assngAccum, assignment) => [
            ...assngAccum,
            {
              map: assignment.map.replace(oldTransitionId, newTransitionId),
              to: assignment.to.replace(oldTransitionId, newTransitionId),
            },
          ], []),
        },
      };
    }, {},
  );

  return { ...properties, targets: updatedTargets };
};

const cleanNodeConnections = (reteNode) => {
  reteNode.data.outputs.forEach((key) => {
    const output = reteNode.outputs.get(key);
    output.node = reteNode;
    output.connections = [];
  });
};

const replaceInputTypeIds = (data) => {
  const { properties } = data;
  if (properties.type === SEND_MESSAGE) {
    const { data: inputData, type: inputType } = properties.input;
    if (inputType === INPUT_TYPES.RADIO || inputType === INPUT_TYPES.CHECKBOX) {
      const updatedInputData = Object.keys(inputData).reduce((accum, key) => ({
        ...accum,
        [key]: { ...inputData[key], id: `${Math.random().toString(36).substring(2, 9)}` },
      }), {});

      return {
        ...data,
        properties: {
          ...data.properties,
          input: {
            ...data.properties.input,
            data: updatedInputData,
          },
        },
      };
    }
  }

  return data;
};

const replaceNodeOutputIds = (reteNode, nodedata, outputs, createOutput) => {
  const { properties } = nodedata;

  if (outputs && properties.type === SEND_MESSAGE) {
    const { type: inputType } = properties.input;
    if (inputType === INPUT_TYPES.RADIO || inputType === INPUT_TYPES.CHECKBOX) {
      const nxtOutput = reteNode.outputs.get('nxt');
      if (nxtOutput) {
        reteNode.removeOutput(nxtOutput);
      }

      outputs.forEach((value) => {
        const randomKey = `${Math.random().toString(36).substring(2, 9)}`;
        const output = createOutput(randomKey, value.name);
        reteNode.addOutput(output);
      });

      return reteNode.outputs;
    }
  }

  return outputs;
};

export const cloneNode = (node, params) => async (dispatch, getState, getContext) => {
  const { botspec } = getBotspecInfo(getState());
  const { editor, createOutput } = getContext();
  const { name, outputs } = node;
  const { data } = botspec.rete_json;
  const nodeData = data[node.id];
  const component = editor.components.get(name);
  const clonedNode = await component.createNode();
  clonedNode.position[0] = params.positionX;
  clonedNode.position[1] = params.positionY;
  editor.addNode(clonedNode);

  const reteNode = editor.nodes.find((editorNode) => editorNode.id === clonedNode.id);
  const clonedData = JSON.parse(JSON.stringify(nodeData));
  const updatedTitle = `${node.name}#${assignName(node.name, editor.nodes)}`;

  const updatedClonedData = replaceInputTypeIds(clonedData);
  const updatedOutputs = replaceNodeOutputIds(reteNode, nodeData, outputs, createOutput);

  let nodeLabels = {};
  updatedOutputs.forEach((value, key) => {
    nodeLabels = { ...nodeLabels, [key]: value.name };
  });

  reteNode.data = {
    ...updatedClonedData,
    ...updatedOutputs ? { outputs: Array.from(updatedOutputs.keys()) } : {},
    outputsLabels: nodeLabels,
    isEntryPoint: false,
  };

  cleanNodeConnections(reteNode);
  await reteNode.update();

  const { type } = updatedClonedData.properties;
  const transitionMapperFunction = type === MAP_RESULT
    ? formatExplicitMapResultVariables : formatMapResultVariables;

  const updatedProperties = transitionMapperFunction(
    node.data.title, updatedTitle, node.id, clonedNode.id, updatedClonedData.properties,
  );

  if (updatedProperties.type === SEND_MESSAGE
    && (updatedProperties.input.type === INPUT_TYPES.RADIO
      || updatedProperties.input.type === INPUT_TYPES.CHECKBOX)) {
    updatedOutputs.forEach((value, key) => {
      Object.keys(updatedProperties.input.data).forEach((input) => {
        if (updatedProperties.input?.data[input]?.name === value.name) {
          updatedProperties.input.data[input].id = key;
        }
      });
    });
  }

  dispatch(saveNodeProperties(reteNode.id, {
    ...updatedProperties,
    title: updatedTitle,
  }));
  editor.selectNode(reteNode);
};
